import { KeyOn } from '@tableau/vizportalclient-common-components';
import { InlineAlertType, InlineAlertWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { Messages } from '../../codegen/Localize';
import { ServerUsers } from '../../userManagement/services/resources/ServerUsers';
import { IServerUser } from '../../userManagement/users/serverApi/types/IServerUser';
import { UserSettingsStyle } from '../../userSettings/styles/UserSettingsStyle';
import { IPersonalAccessToken } from '../serverApi/types/personalAccess/IPersonalAccessToken';
import { CreatePersonalAccessTokensSection } from './CreatePersonalAccessTokenSection';
import { ListPersonalAccessTokensSection } from './ListPersonalAccessTokenSection';

const DEEPLINK_ID = 'personal-access-token';

export interface PersonalAccessTokensSectionProps {
  canCreatePersonalAccessTokens: boolean;
  serverUser: IServerUser;
}

function PersonalAccessTokensSectionFC(props: PersonalAccessTokensSectionProps) {
  const [personalAccessTokens, setPersonalAccessTokens] = React.useState([] as IPersonalAccessToken[]);
  const [personalAccessTokenCreationAllowed, setPersonalAccessTokenCreationAllowed] = React.useState(Boolean);

  async function refreshPersonalAccessTokens(userId: string): Promise<IPersonalAccessToken[]> {
    const tokens = await ServerUsers.instance.getPersonalAccessTokens(userId);
    setPersonalAccessTokens(tokens);
    return tokens;
  }

  async function canCreatePersonalAccessTokensBasedOnSiteSettings() {
    const result = await ServerUsers.instance.checkPersonalAccessTokenCreationIsAllowed();
    if (!result.errors) {
      setPersonalAccessTokenCreationAllowed(true);
    } else {
      // For now, regardless of the error code, disallow pat creation if any error
      setPersonalAccessTokenCreationAllowed(false);
    }
  }

  function getPatCreateSection() {
    if (!props.canCreatePersonalAccessTokens) {
      return <></>;
    }

    var sectionValue = <></>;

    if (personalAccessTokenCreationAllowed) {
      sectionValue = <CreatePersonalAccessTokensSection
        personalAccessTokens={personalAccessTokens}
        refreshTokenList={() => refreshPersonalAccessTokens(props.serverUser.id)} />;
    } else if (personalAccessTokens.length === 0) {
      // User can't create PAT and has no PAT to start with
      sectionValue = <InlineAlertWidget testId={'pat-creation-message'} type={InlineAlertType.Info}>
        {Messages.personalAccessTokensCannotCreateInfo()}
      </InlineAlertWidget>;
    } else {
      // User can't create PAT now but had PAT's created earlier
      sectionValue = <InlineAlertWidget testId={'pat-creation-warning'} type={InlineAlertType.Warning}>
        {Messages.personalAccessTokensCannotCreateWarning()}
      </InlineAlertWidget>;
    }

    return <div className={UserSettingsStyle.detailsValue}>
        {sectionValue}
      </div>;
  }

  React.useEffect(() => {
    setPersonalAccessTokens([]);
    refreshPersonalAccessTokens(props.serverUser.id);
    setPersonalAccessTokenCreationAllowed(true);
    canCreatePersonalAccessTokensBasedOnSiteSettings();
  }, [props.serverUser.id]);

  return (
    <DeepLink deepLinkId={DEEPLINK_ID}>
        <div data-tb-test-id="personal-access-token-section" style={{ display: 'flex' }}>
            <div className={UserSettingsStyle.settingsLabelText}>
              <h4>{Messages.personalAccessTokenSection()}</h4>
            </div>
            <div className={UserSettingsStyle.settingsLabelDescription}>
              <div className={UserSettingsStyle.detailsValue}>
                {Messages.personalAccessTokenDescription()}
                &nbsp;
            <TextLinkWidget
              testId={'allow-pat-creation-description'}
              target={'_blank'}
              className={learnMoreStyle}
              href={HelpService.getServerHelpUrlByCategory('personal-access-token')}
              aria-label={Messages.personalAccessTokenLearnMoreAriaLabel()}>
              {Messages.learnMore()}
            </TextLinkWidget>
            </div>
            {getPatCreateSection()}
            <div className={UserSettingsStyle.detailsValue}>
              <ListPersonalAccessTokensSection
                serverUser={props.serverUser}
                personalAccessTokens={personalAccessTokens}
                refreshTokenList={refreshPersonalAccessTokens} />
            </div>
        </div>
        </div>
    </DeepLink>
  );
}

export const PersonalAccessTokensSection = KeyOn(
  PersonalAccessTokensSectionFC,
  (props: PersonalAccessTokensSectionProps) => props.serverUser.id);

export const PersonalAccessTokenSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.personalAccessTokenSection(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  tags:  () => { return [Messages.personalAccessTokenDescription(), Messages.userSettings_Security_Header()]; },
  isAdmin: false,
  isVisible: () =>
    // NOTE: skipping passThroughProps.isViewerSelf aka getSessionManagementServiceInstance()
    // .isSessionUserSelf(username, domainName) since we will direct user to their own page so we don't
    // need to use this field

    FeatureFlags.personalAccessTokensEnabled
});